import { AlertTriangle, CheckCircle2, CloudOff, RefreshCw } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  api,
  CONNECTIVITY_CHANGED_EVENT,
  OFFLINE_DATA_CHANGED_EVENT,
  SECURE_SYNC_REQUESTED_EVENT,
  type SynchronizationVersion,
} from "../lib/api";
import {
  offlinePersistence,
  type OfflineSnapshot,
} from "../lib/offline";
import {
  getVaultSyncSnapshot,
  resolveVaultConflict,
  subscribeVaultSync,
  synchronizeSecureReplica,
  type VaultSyncSnapshot,
} from "../lib/vault-sync";
import { Modal } from "./Modal";

const initialSnapshot: OfflineSnapshot = {
  status: "sync",
  pending_count: 0,
  syncing: false,
  error: null,
};

const initialVaultSnapshot: VaultSyncSnapshot = {
  status: "idle",
  error: null,
};

const SYNCHRONIZATION_CHECK_INTERVAL_MS = 10_000;

interface SynchronizationCheckResult {
  version: SynchronizationVersion | null;
  checkedAt: string | null;
  error: string | null;
}

let knownSynchronizationVersion: SynchronizationVersion | null = null;
let lastSuccessfulCheckAt: string | null = null;
let initialSynchronizationCompleted = false;
let synchronizationCheckPromise: Promise<SynchronizationCheckResult> | null = null;
let forceAfterCurrentCheck = false;

function synchronizationErrorMessage(error: unknown): string {
  return error instanceof Error
    ? error.message
    : "La vérification des mises à jour a échoué.";
}

async function performSynchronizationCheck(
  initial: boolean,
  force: boolean,
): Promise<SynchronizationCheckResult> {
  try {
    if (initial && !initialSynchronizationCompleted) {
      const version = await api.getSynchronizationVersion();
      // Mémoriser la révision observée avant la récupération des données.
      // Une mutation qui survient pendant cette récupération restera ainsi
      // détectable au contrôle suivant, au lieu de devenir à tort la base
      // d'une copie locale potentiellement plus ancienne.
      knownSynchronizationVersion = version;
      await synchronizeSecureReplica();
      initialSynchronizationCompleted = true;
      const vault = getVaultSyncSnapshot();
      if (vault.status !== "error" && vault.status !== "conflict") {
        lastSuccessfulCheckAt = new Date().toISOString();
      }
      return {
        version,
        checkedAt: lastSuccessfulCheckAt,
        error: null,
      };
    }

    const version = await api.getSynchronizationVersion();
    const revisionChanged =
      knownSynchronizationVersion !== null &&
      version.revision !== knownSynchronizationVersion.revision;

    if (force || revisionChanged) {
      await synchronizeSecureReplica();
      const vault = getVaultSyncSnapshot();
      if (vault.status === "conflict") {
        // Le conflit doit rester actionnable sans qu'un GET métier ne modifie
        // la copie locale avant le choix explicite de l'utilisateur.
        knownSynchronizationVersion = version;
        return {
          version,
          checkedAt: lastSuccessfulCheckAt,
          error: null,
        };
      }
      if (vault.status === "error" || vault.status === "pending") {
        return {
          version: knownSynchronizationVersion,
          checkedAt: lastSuccessfulCheckAt,
          error: null,
        };
      }
      window.dispatchEvent(new Event(OFFLINE_DATA_CHANGED_EVENT));
    }

    knownSynchronizationVersion = version;
    lastSuccessfulCheckAt = new Date().toISOString();
    return {
      version,
      checkedAt: lastSuccessfulCheckAt,
      error: null,
    };
  } catch (error) {
    return {
      version: knownSynchronizationVersion,
      checkedAt: lastSuccessfulCheckAt,
      error: synchronizationErrorMessage(error),
    };
  }
}

function requestSynchronizationCheck(options: {
  initial?: boolean;
  force?: boolean;
} = {}): Promise<SynchronizationCheckResult> {
  const initial = options.initial === true;
  const force = options.force === true;
  if (synchronizationCheckPromise) {
    if (force) forceAfterCurrentCheck = true;
    return synchronizationCheckPromise;
  }

  synchronizationCheckPromise = (async () => {
    let result = await performSynchronizationCheck(initial, force);
    while (forceAfterCurrentCheck) {
      forceAfterCurrentCheck = false;
      result = await performSynchronizationCheck(false, true);
    }
    return result;
  })().finally(() => {
    synchronizationCheckPromise = null;
  });
  return synchronizationCheckPromise;
}

function browserIsOnline(): boolean {
  return typeof navigator === "undefined" || navigator.onLine !== false;
}

function pendingLabel(count: number): string {
  return `${count} action${count > 1 ? "s" : ""} en attente`;
}

function updatedAtLabel(value: string | null): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat("fr-FR", {
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

export function OfflineStatus() {
  const [online, setOnline] = useState(browserIsOnline);
  const [snapshot, setSnapshot] = useState<OfflineSnapshot>(initialSnapshot);
  const [vaultSnapshot, setVaultSnapshot] = useState<VaultSyncSnapshot>(
    initialVaultSnapshot,
  );
  const [synchronizationVersion, setSynchronizationVersion] =
    useState<SynchronizationVersion | null>(knownSynchronizationVersion);
  const [lastCheckedAt, setLastCheckedAt] =
    useState<string | null>(lastSuccessfulCheckAt);
  const [checkError, setCheckError] = useState<string | null>(null);
  const [manualRefresh, setManualRefresh] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const mounted = useRef(true);

  const checkForUpdates = useCallback(async (
    options: { initial?: boolean; force?: boolean; manual?: boolean } = {},
  ) => {
    if (
      !browserIsOnline() ||
      typeof document !== "undefined" && document.visibilityState !== "visible"
    ) return;
    if (options.manual && mounted.current) setManualRefresh(true);
    const result = await requestSynchronizationCheck(options);
    if (!mounted.current) return;
    setSynchronizationVersion(result.version);
    setLastCheckedAt(result.checkedAt);
    setCheckError(result.error);
    if (options.manual) setManualRefresh(false);
  }, []);

  useEffect(() => offlinePersistence.subscribe(setSnapshot), []);
  useEffect(() => subscribeVaultSync(setVaultSnapshot), []);

  useEffect(() => {
    mounted.current = true;
    let intervalId: number | null = null;

    const stopPolling = () => {
      if (intervalId === null) return;
      window.clearInterval(intervalId);
      intervalId = null;
    };
    const startPolling = (immediate = true) => {
      stopPolling();
      if (
        !browserIsOnline() ||
        document.visibilityState !== "visible"
      ) return;
      if (immediate) void checkForUpdates({ initial: true });
      intervalId = window.setInterval(() => {
        if (
          browserIsOnline() &&
          document.visibilityState === "visible"
        ) {
          void checkForUpdates();
        }
      }, SYNCHRONIZATION_CHECK_INTERVAL_MS);
    };
    const handleOnline = () => {
      setOnline(true);
      startPolling(false);
      // Le retour réseau doit aussi pousser les actions créées hors ligne,
      // même si la révision distante n'a pas encore changé.
      void checkForUpdates({ force: true });
    };
    const handleOffline = () => {
      setOnline(false);
      stopPolling();
    };
    const handleConnectivity = (event: Event) => {
      const nextOnline =
        (event as CustomEvent<{ online?: boolean }>).detail?.online ??
        browserIsOnline();
      setOnline(nextOnline);
      // Cet événement est émis pendant un appel API : en cas de succès, cet
      // appel constitue déjà la vérification immédiate. Ne relancer ici que le
      // minuteur évite une seconde requête réentrante identique.
      if (nextOnline) startPolling(false);
      else if (
        browserIsOnline() &&
        document.visibilityState === "visible"
      ) {
        // Une erreur de transport peut être transitoire sans que le navigateur
        // n'émette ensuite d'événement `online` (navigator.onLine est resté à
        // true). Garder un contrôle différé permet alors de reprendre seul.
        startPolling(false);
      } else stopPolling();
    };
    const handleVisibility = () => {
      if (document.visibilityState === "visible" && browserIsOnline()) {
        setOnline(true);
        startPolling();
      } else stopPolling();
    };
    const handleFocus = () => startPolling();
    const handleSynchronizationRequest = () => {
      // Une action locale ne doit pas dépendre de la visibilité de l’onglet ni
      // d’une variation de révision D1 pour atteindre vault-sync. Celui-ci
      // coalesce lui-même les signaux reçus pendant une passe en cours.
      void synchronizeSecureReplica();
    };

    window.addEventListener("online", handleOnline, { passive: true });
    window.addEventListener("offline", handleOffline, { passive: true });
    window.addEventListener("focus", handleFocus, { passive: true });
    window.addEventListener(CONNECTIVITY_CHANGED_EVENT, handleConnectivity);
    window.addEventListener(
      SECURE_SYNC_REQUESTED_EVENT,
      handleSynchronizationRequest,
    );
    document.addEventListener("visibilitychange", handleVisibility, {
      passive: true,
    });

    startPolling();

    return () => {
      mounted.current = false;
      stopPolling();
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener(CONNECTIVITY_CHANGED_EVENT, handleConnectivity);
      window.removeEventListener(
        SECURE_SYNC_REQUESTED_EVENT,
        handleSynchronizationRequest,
      );
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [checkForUpdates]);

  const abandonFailedAction = useCallback(async () => {
    const operations = await offlinePersistence.listQueuedMovements();
    const failed =
      operations.find((operation) => operation.status === "error") ??
      operations[0];
    if (!failed) return;

    const cancelled = await offlinePersistence.cancelQueuedMovement(
      failed.client_operation_id,
    );
    if (!cancelled) return;

    window.dispatchEvent(new Event(OFFLINE_DATA_CHANGED_EVENT));
    if (browserIsOnline()) {
      await checkForUpdates({ force: true, manual: true });
    }
  }, [checkForUpdates]);

  const vaultConflict = online && vaultSnapshot.status === "conflict";
  const vaultError = online && vaultSnapshot.status === "error";
  const vaultSyncing = online && vaultSnapshot.status === "syncing";
  const synchronizing = snapshot.syncing || vaultSyncing;
  const pollingError = online && !vaultConflict && !vaultError && checkError;
  const upToDate =
    online &&
    snapshot.pending_count === 0 &&
    !snapshot.error &&
    !pollingError &&
    (vaultSnapshot.status === "up_to_date" ||
      (vaultSnapshot.status === "idle" && synchronizationVersion !== null));

  if (
    online &&
    snapshot.pending_count === 0 &&
    !snapshot.error &&
    vaultSnapshot.status === "idle" &&
    synchronizationVersion === null &&
    !pollingError
  ) return null;

  // Une panne réseau détectée par fetch doit rester un état « hors connexion »
  // simple. Le détail d'un conflit n'est utile que lorsque le réseau répond.
  const isError =
    online && (
      (snapshot.status === "error" && Boolean(snapshot.error)) ||
      vaultConflict ||
      vaultError ||
      Boolean(pollingError)
    );
  const label = synchronizing
    ? "Synchronisation…"
    : vaultConflict
      ? "Conflit de synchronisation"
    : isError
      ? "Synchronisation à vérifier"
      : upToDate
        ? "À jour"
      : online
        ? "À synchroniser"
        : "Hors ligne";
  const lastCheckedLabel = updatedAtLabel(lastCheckedAt);

  const statusIcon = (size: number) => isError ? (
    <AlertTriangle size={size} />
  ) : synchronizing ? (
    <RefreshCw className="offline-status-spinner" size={size} />
  ) : upToDate ? (
    <CheckCircle2 size={size} />
  ) : (
    <CloudOff size={size} />
  );

  return (
    <>
      <aside
        className={`offline-status${isError ? " offline-status-error" : ""}${upToDate ? " offline-status-success" : ""}`}
        aria-live={detailsOpen ? undefined : "polite"}
        aria-atomic="true"
      >
        {upToDate ? (
          <button
            className="offline-status-mobile-trigger"
            type="button"
            aria-label="À jour. Afficher les informations de synchronisation"
            aria-haspopup="dialog"
            aria-expanded={detailsOpen}
            title="À jour — voir la synchronisation"
            onClick={() => setDetailsOpen(true)}
          >
            <span className="offline-status-mobile-dot" aria-hidden="true" />
          </button>
        ) : null}
        <div className="offline-status-inner">
          <span className="offline-status-icon" aria-hidden="true">
            {statusIcon(18)}
          </span>
          <div className="offline-status-copy">
            <strong>{label}</strong>
            {snapshot.pending_count > 0 ? (
              <span>{pendingLabel(snapshot.pending_count)}</span>
            ) : null}
            {lastCheckedLabel ? (
              <span className="offline-status-updated">
                Mis à jour à {lastCheckedLabel}
              </span>
            ) : null}
            {isError ? (
              <span>{vaultSnapshot.error ?? snapshot.error ?? pollingError}</span>
            ) : null}
          </div>
          {vaultConflict ? (
            <div className="offline-status-actions">
              <button type="button" onClick={() => void resolveVaultConflict("local")}>
                Garder cet appareil
              </button>
              <button type="button" onClick={() => void resolveVaultConflict("remote")}>
                Utiliser le serveur
              </button>
            </div>
          ) : isError ? (
            <div className="offline-status-actions">
              <button
                type="button"
                onClick={() => void checkForUpdates({ force: true, manual: true })}
                disabled={manualRefresh || synchronizing}
              >
                Réessayer
              </button>
              {snapshot.pending_count > 0 ? (
                <button type="button" onClick={() => void abandonFailedAction()}>
                  Abandonner l’action
                </button>
              ) : null}
            </div>
          ) : (
            <div className="offline-status-actions">
              <button
                type="button"
                onClick={() => void checkForUpdates({ force: true, manual: true })}
                disabled={
                  !browserIsOnline() ||
                  manualRefresh ||
                  synchronizing
                }
              >
                <RefreshCw
                  className={manualRefresh ? "offline-status-spinner" : undefined}
                  size={14}
                  aria-hidden="true"
                />
                Actualiser
              </button>
            </div>
          )}
        </div>
      </aside>

      <Modal
        open={detailsOpen}
        title="Synchronisation"
        description="État des données de cette cave sur cet appareil."
        onClose={() => setDetailsOpen(false)}
        size="small"
      >
        <div
          className={`offline-status-details${isError ? " offline-status-details-error" : ""}${upToDate ? " offline-status-details-success" : ""}`}
          role="status"
          aria-live="polite"
          aria-atomic="true"
        >
          <span className="offline-status-details-icon" aria-hidden="true">
            {statusIcon(20)}
          </span>
          <div className="offline-status-details-copy">
            <strong>{label}</strong>
            {upToDate ? (
              <p>Les données de votre cave sont synchronisées sur cet appareil.</p>
            ) : !online ? (
              <p>Votre cave reste disponible ici et se synchronisera au retour du réseau.</p>
            ) : synchronizing ? (
              <p>Les dernières modifications sont en cours de synchronisation.</p>
            ) : null}
            {snapshot.pending_count > 0 ? (
              <p>{pendingLabel(snapshot.pending_count)}</p>
            ) : null}
            {lastCheckedLabel ? (
              <p>Dernière vérification à {lastCheckedLabel}</p>
            ) : null}
            {isError ? (
              <p>{vaultSnapshot.error ?? snapshot.error ?? pollingError}</p>
            ) : null}
          </div>
        </div>

        {vaultConflict ? (
          <div className="modal-actions offline-status-modal-actions">
            <button
              className="button button-secondary"
              type="button"
              onClick={() => void resolveVaultConflict("local")}
            >
              Garder cet appareil
            </button>
            <button
              className="button button-secondary"
              type="button"
              onClick={() => void resolveVaultConflict("remote")}
            >
              Utiliser le serveur
            </button>
          </div>
        ) : isError ? (
          <div className="modal-actions offline-status-modal-actions">
            <button
              className="button button-primary"
              type="button"
              onClick={() => void checkForUpdates({ force: true, manual: true })}
              disabled={manualRefresh || synchronizing}
            >
              Réessayer
            </button>
            {snapshot.pending_count > 0 ? (
              <button
                className="button button-secondary"
                type="button"
                onClick={() => void abandonFailedAction()}
              >
                Abandonner l’action
              </button>
            ) : null}
          </div>
        ) : (
          <div className="modal-actions offline-status-modal-actions">
            <button
              className="button button-secondary"
              type="button"
              onClick={() => void checkForUpdates({ force: true, manual: true })}
              disabled={
                !browserIsOnline() ||
                manualRefresh ||
                synchronizing
              }
            >
              <RefreshCw
                className={manualRefresh ? "offline-status-spinner" : undefined}
                size={16}
                aria-hidden="true"
              />
              Actualiser
            </button>
          </div>
        )}
      </Modal>
    </>
  );
}
